// src/lib/api.js
import axios from 'axios';
import { getAccess, getRefresh, saveAuth, clearAuth } from './auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
export const api = axios.create({ baseURL });

// -- util interne : lire le cartId côté front (localStorage OU sessionStorage)
function getCartId() {
  try {
    return (
      localStorage.getItem('cartId') ||
      sessionStorage.getItem('cartId') ||
      null
    );
  } catch {
    return null;
  }
}

// REQUEST: ajoute systématiquement Bearer + X-Cart-Id
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  const token = getAccess();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const cartId = getCartId();
  if (cartId && !config.headers['X-Cart-Id']) {
    config.headers['X-Cart-Id'] = cartId;
  }

  return config;
});

// ======================= Refresh token automatique si 401 =======================
let isRefreshing = false;
let pending = []; // callbacks à rejouer quand on a un nouveau token

function onRefreshed(newToken) {
  pending.forEach((cb) => cb(newToken));
  pending = [];
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err?.config;
    const status = err?.response?.status;
    const serverMsg = String(err?.response?.data?.message || '');

    // ⛔️ Cas "Account disabled" : logout immédiat + redirection dure
    if (status === 403 && serverMsg.toLowerCase().includes('account disabled')) {
      try { clearAuth(); } catch {}
      if (typeof window !== 'undefined') window.location.assign('/services');
      return Promise.reject(err);
    }

    // On ne tente le refresh que sur 401, et une seule fois par requête
    if (!original || status !== 401 || original._retry) {
      return Promise.reject(err);
    }

    // Marque pour éviter boucle infinie
    original._retry = true;

    // Si on est déjà en train de rafraîchir: on met la requête en attente
    if (isRefreshing) {
      return new Promise((resolve) => {
        pending.push((t) => {
          // réinjecter Authorization + X-Cart-Id
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${t}`;
          const cid = getCartId();
          if (cid) original.headers['X-Cart-Id'] = cid;
          resolve(api(original));
        });
      });
    }

    // Démarre un refresh
    isRefreshing = true;
    try {
      const refresh = getRefresh();
      if (!refresh) {
        // pas de refresh => session terminée
        isRefreshing = false;
        clearAuth();
        return Promise.reject(err);
      }

      // IMPORTANT: on envoie aussi X-Cart-Id sur le refresh
      const cid = getCartId();
      const r = await axios.post(
        `${baseURL}/auth/refresh`,
        { refresh },
        { headers: cid ? { 'X-Cart-Id': cid } : undefined }
      );

      const newAccess = r?.data?.access;
      if (!newAccess) {
        isRefreshing = false;
        clearAuth();
        return Promise.reject(err);
      }

      // Sauvegarder le nouvel access en gardant le refresh et l'user stocké
      const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
      saveAuth({ access: newAccess, refresh, user: currentUser });

      isRefreshing = false;
      onRefreshed(newAccess);

      // Rejouer la requête initiale avec les bons headers
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${newAccess}`;
      if (cid) original.headers['X-Cart-Id'] = cid;
      return api(original);
    } catch (e) {
      isRefreshing = false;

      // Si le refresh échoue parce que le compte est désactivé
      const st = e?.response?.status;
      const msg = String(e?.response?.data?.message || '');
      if (st === 403 && msg.toLowerCase().includes('account disabled')) {
        clearAuth();
        if (typeof window !== 'undefined') window.location.assign('/services');
      } else {
        clearAuth();
      }
      return Promise.reject(e);
    }
  }
);